import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, HelpCircle, Lock, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fireBurst } from "@/lib/celebrate";
import ApertureLoader from "@/components/ApertureLoader";
import { StatusBadge } from "@/components/StatusBadge";
import { TierBadge } from "@/components/TierBadge";
import { ChecklistCard } from "@/components/photographer/ChecklistCard";
import { HeroApproved } from "@/components/photographer/HeroApproved";
import { HeroWelcome } from "@/components/photographer/HeroWelcome";
import { ProgressDial } from "@/components/photographer/ProgressDial";
import { ServiceAreaCard } from "@/components/photographer/ServiceAreaCard";
import { StatusTimeline } from "@/components/photographer/StatusTimeline";
import { TierRateCard } from "@/components/photographer/TierRateCard";
import { HERO_IMAGE, PHASES } from "@/components/photographer/constants";
import PhotographerTutorial, { TUTORIAL_SEEN_KEY } from "@/components/photographer/PhotographerTutorial";

// Heavy: framer-motion sequence, canvas-confetti, watermarked ID card.
// Only pulled in when a photographer actually hits 100% (or replays via footer).
const OnboardingCompleteCelebration = lazy(() =>
  import("@/components/OnboardingCompleteCelebration")
);

export default function PhotographerDashboard() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingArea, setSavingArea] = useState(false);
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState("");
  const [showCertCelebration, setShowCertCelebration] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const milestoneFiredRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/photographer/dashboard");
      setData(data);
      setZip(data.user.base_zip_code || "");
      setRadius(data.user.driving_radius_miles ? String(data.user.driving_radius_miles) : "100");
    } catch (e) {
      toast.error("Could not load your dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-open the onboarding tour the FIRST time a photographer lands on the
  // dashboard. Once they finish or dismiss it, the localStorage flag prevents
  // re-showing on subsequent logins. The "Take the tour" footer button
  // manually re-opens it anytime — no permanent lockout.
  useEffect(() => {
    if (loading || !data) return;
    try {
      if (!localStorage.getItem(TUTORIAL_SEEN_KEY)) {
        // Small delay so the dashboard renders + settles before the overlay
        // slides in — feels less like a modal ambush.
        const t = setTimeout(() => setShowTutorial(true), 700);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage disabled — skip auto-open */ }
  }, [loading, data]);

  // "Core" = everything except the final scheduling step. The finale is timed
  // to celebrate core completion so the scheduling call feels like the ceremony
  // AFTER the ribbon-cutting, not part of the checklist grind.
  const coreItemsSummary = useMemo(() => {
    if (!data) return { done: 0, total: 0 };
    const core = data.items.filter((i) => i.phase !== "scheduling");
    return {
      done: core.filter((i) => i.completed).length,
      total: core.length,
    };
  }, [data]);

  const coreComplete = useMemo(() => {
    if (!data) return false;
    return (
      coreItemsSummary.total > 0 &&
      coreItemsSummary.done >= coreItemsSummary.total &&
      data.service_area_complete
    );
  }, [data, coreItemsSummary]);

  // Reactive finale trigger — fires the moment CORE completion is reached (the
  // scheduling call is deliberately excluded). Guarded by a ref so it only
  // fires once per session. No localStorage lockout — if they refresh right
  // after completing, they get to see it again (better than accidental
  // permanent lock-out).
  useEffect(() => {
    if (!data || milestoneFiredRef.current) return;
    if (!coreComplete) return;
    milestoneFiredRef.current = true;
    // Slight delay lets the item-burst confetti settle before the finale takes over.
    setTimeout(() => setShowCertCelebration(true), 450);
  }, [data, coreComplete]);

  // Listen for Calendly's `event_scheduled` postMessage. The instant the
  // photographer books their onboarding call inside the popup widget, we
  // auto-mark the schedule item complete (no manual checkbox tick needed) —
  // this is the true finish line for the whole onboarding journey.
  useEffect(() => {
    const isCalendly = (e) =>
      e && e.origin === "https://calendly.com" &&
      e.data && typeof e.data.event === "string" &&
      e.data.event.indexOf("calendly.") === 0;

    const onMessage = (e) => {
      if (!isCalendly(e)) return;
      if (e.data.event !== "calendly.event_scheduled") return;
      // Snapshot the schedule item from the latest state we have. Skip
      // silently if there isn't one seeded yet or it's already checked.
      const scheduleItem = data?.items?.find(
        (i) => i.action_type === "schedule" && !i.completed,
      );
      if (!scheduleItem) return;
      toggleItem(scheduleItem.id, true);
      toast.success("Onboarding call booked — you're all set!");
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load();
    };
    const interval = setInterval(tick, 20000);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  const toggleItem = async (itemId, completed) => {
    setData((prev) => prev ? { ...prev, items: prev.items.map((i) => (i.id === itemId ? { ...i, completed } : i)) } : prev);
    // Fire a small confetti burst from the checkbox when a fresh item is completed.
    if (completed) {
      const el = document.querySelector(`[data-testid="checklist-toggle-${itemId}"]`);
      fireBurst(el);
    }
    try {
      await api.post(`/photographer/checklist/${itemId}/toggle`, { completed });
      await load();
      await refresh();
      if (completed) toast.success("Marked complete.");
    } catch (e) {
      toast.error("Could not update this item.");
      load();
    }
  };

  const markViewed = async (itemId) => {
    try {
      await api.post(`/photographer/checklist/${itemId}/mark-viewed`);
      setData((prev) => prev ? { ...prev, items: prev.items.map((i) => (i.id === itemId ? { ...i, viewed_at: new Date().toISOString() } : i)) } : prev);
    } catch (err) {
      console.error("mark-viewed request failed", err);
    }
  };

  const uploadAttachment = async (itemId, file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: updated } = await api.post(
        `/photographer/checklist/${itemId}/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Optimistically merge server-returned item into state; then reload for full progress recompute.
      setData((prev) => prev ? { ...prev, items: prev.items.map((i) => (i.id === itemId ? { ...i, ...updated } : i)) } : prev);
      toast.success("Uploaded — item marked complete.");
      const el = document.querySelector(`[data-testid="checklist-toggle-${itemId}"]`);
      fireBurst(el);
      await load();
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed. Try again.";
      toast.error(typeof msg === "string" ? msg : "Upload failed. Try again.");
    }
  };

  const removeAttachment = async (itemId, fileId) => {
    try {
      // Optimistically drop that one file from the item's attachments list.
      setData((prev) => prev ? {
        ...prev,
        items: prev.items.map((i) =>
          i.id === itemId
            ? { ...i, attachments: (i.attachments || []).filter((a) => a.id !== fileId) }
            : i
        ),
      } : prev);
      await api.delete(`/photographer/checklist/${itemId}/attachment/${fileId}`);
      toast.success("File removed.");
      // Reload so per-item completion state re-syncs (upload-only items un-mark themselves
      // if that was the last file).
      await load();
      await refresh();
    } catch (e) {
      toast.error("Could not remove that file.");
      // Best-effort rollback via a full reload.
      await load();
    }
  };

  const saveServiceArea = async (e) => {
    e.preventDefault();
    if (!zip.trim() || !radius) return;
    setSavingArea(true);
    try {
      await api.patch("/photographer/service-area", {
        base_zip_code: zip.trim(),
        driving_radius_miles: parseInt(radius, 10),
      });
      toast.success("Service area saved.");
      await load();
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save service area.";
      toast.error(typeof msg === "string" ? msg : "Could not save service area.");
    } finally {
      setSavingArea(false);
    }
  };

  const groupedByPhase = useMemo(() => {
    if (!data) return {};
    return data.items.reduce((acc, item) => {
      (acc[item.phase] = acc[item.phase] || []).push(item);
      return acc;
    }, {});
  }, [data]);

  const nextItem = useMemo(() => {
    if (!data) return null;
    return data.items.find((i) => !i.completed) || null;
  }, [data]);

  // Final Exam unlocks once every Documents-phase item is complete.
  // Tax & Payments items don't gate the exam.
  const examLocked = useMemo(() => {
    if (!data) return true;
    return (data.items || []).some((i) => i.phase === "documents" && !i.completed);
  }, [data]);

  const continueOnboarding = () => {
    const targetId = nextItem
      ? `item-${nextItem.id}`
      : (!data?.service_area_complete ? "service-area" : null);
    if (!targetId) return;
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Brief highlight pulse so the user's eye lands in the right place
    el.style.transition = "box-shadow 240ms ease-out";
    el.style.boxShadow = "0 0 0 3px rgba(107, 15, 26, 0.35)";
    setTimeout(() => { el.style.boxShadow = ""; }, 1400);
  };

  if (loading) {
    return <ApertureLoader label="Loading your onboarding…" />;
  }

  if (!data) return null;

  const percent = data.percent_complete;
  const firstName = (data.user.name || data.user.email.split("@")[0]).split(" ")[0];
  const isApproved = data.user.status === "approved";
  const hubUrl = (process.env.REACT_APP_SHOOTS_HUB_URL || "").trim();

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="photographer-dashboard">
      {/* Lazy — only mounts when the milestone fires (or the dev replay button is used). */}
      {showCertCelebration && (
        <Suspense fallback={null}>
          <OnboardingCompleteCelebration
            open={showCertCelebration}
            onClose={() => setShowCertCelebration(false)}
            name={data.user.name || firstName}
            email={data.user.email}
            tier={data.user.tier}
            rate={data.user.tier_rate ?? data.user.rate_per_shoot ?? data.user.rate}
            radiusMiles={data.user.driving_radius_miles}
            approved={isApproved}
            hubUrl={hubUrl}
          />
        </Suspense>
      )}

      {/* HERO */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-sm overflow-hidden card-surface"
      >
        {/* Decorative image: kept very faint + covered by a strong cream veil so text stays legible */}
        <img
          src={HERO_IMAGE}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(245,235,217,0.96) 0%, rgba(245,235,217,0.88) 55%, rgba(245,235,217,0.7) 100%)",
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 p-8 md:p-14">
          {/* TOP RIBBON */}
          <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={data.user.status} />
              <TierBadge tier={data.user.tier} showRate size="lg" />
            </div>
            <div className="hidden sm:flex items-center gap-2 font-mono-tag text-[9px] text-[#78584B]">
              <CalendarDays className="w-3 h-3" strokeWidth={2} />
              {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </div>
          </div>

          {/* MAIN: greeting + circular progress dial (or big APPROVED once admin approves) */}
          <div className="grid md:grid-cols-[1fr_auto] gap-10 md:gap-14 items-center">
            {isApproved ? (
              <HeroApproved
                firstName={firstName}
                approvedAt={data.user.approved_at}
                hubUrl={hubUrl}
              />
            ) : (
              <HeroWelcome
                firstName={firstName}
                completedItems={data.completed_items}
                totalItems={data.total_items}
                serviceAreaComplete={data.service_area_complete}
                nextItem={nextItem}
                onContinue={continueOnboarding}
              />
            )}

            {/* Circular progress dial */}
            <div className="flex md:justify-end" data-testid="progress-dial">
              <ProgressDial percent={percent} completed={data.completed_items} total={data.total_items} />
            </div>
          </div>

          {/* Callout when completed but not yet approved (approval banner already handled above) */}
          {data.user.status === "completed" && (
            <div
              className="mt-10 rounded-sm p-5 flex items-start gap-3 border border-[#6B0F1A]/40 bg-[#6B0F1A]/8"
              data-testid="onboarding-complete-callout"
            >
              <Sparkles className="w-4 h-4 mt-0.5 text-[#6B0F1A]" strokeWidth={1.75} />
              <div>
                <div className="font-mono-tag text-[10px] text-[#6B0F1A]">
                  Onboarding Complete · Pending Admin Review
                </div>
                <p className="text-xs text-[#4D3F35] mt-1 leading-relaxed">
                  Nicely done, {firstName}. Every step is checked off. A Reelty admin will review your
                  documents and approve your account shortly — you'll get access to the Property Shoots Hub the moment they do.
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.section>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-8 space-y-10" data-testid="checklist-column">
          {PHASES.map(({ key, label, description, icon: PhaseIcon }) => {
            const items = groupedByPhase[key] || [];
            if (!items.length) return null;
            const done = items.filter((i) => i.completed).length;
            // Scheduling is deliberately gated — it only unlocks once every
            // other checklist item + the service area are done, so it feels
            // like the final ceremony after the finale rather than a task on
            // the pile.
            const isSchedulingPhase = key === "scheduling";
            const locked = isSchedulingPhase && !coreComplete;
            return (
              <section key={key} data-testid={`phase-${key}`}>
                <header className="flex items-baseline justify-between mb-4 pb-3 border-b border-[#1A0F0A]/12">
                  <div className="flex items-center gap-3">
                    <PhaseIcon className={`w-4 h-4 ${locked ? "text-[#78584B]" : "text-[#6B0F1A]"}`} strokeWidth={1.75} />
                    <div>
                      <h2 className={`font-display text-xl font-bold tracking-tight ${locked ? "text-[#78584B]" : "text-[#1A0F0A]"}`}>{label}</h2>
                      <p className="text-xs text-[#78584B] mt-0.5">{description}</p>
                    </div>
                  </div>
                  <span className="font-mono-tag text-[10px] text-[#78584B] flex items-center gap-1.5">
                    {locked && <Lock className="w-3 h-3" strokeWidth={2} />}
                    {locked ? "LOCKED" : `${done} / ${items.length}`}
                  </span>
                </header>
                {locked ? (
                  <div
                    data-testid="scheduling-locked-preview"
                    className="card-surface rounded-sm p-6 flex items-start gap-4 border-dashed"
                  >
                    <Lock className="w-5 h-5 text-[#78584B] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                    <div>
                      <div className="font-mono-tag text-[10px] text-[#78584B] mb-1">FINAL STEP</div>
                      <h3 className="font-display text-lg font-semibold text-[#1A0F0A]">
                        Book your onboarding call with the Reelty team
                      </h3>
                      <p className="text-sm text-[#4D3F35] mt-1.5 leading-relaxed">
                        This unlocks the moment every step above is checked off and your
                        service area is saved. Finish the checklist first — you're almost there.
                      </p>
                      <div className="mt-3 font-mono-tag text-[10px] text-[#78584B]">
                        {coreItemsSummary.done} / {coreItemsSummary.total} STEPS DONE
                        {!data.service_area_complete && " · SERVICE AREA PENDING"}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((it, idx) => (
                      <motion.div key={it.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * idx }}>
                        <ChecklistCard
                          item={it}
                          onToggle={toggleItem}
                          onMarkViewed={markViewed}
                          onUpload={uploadAttachment}
                          onRemoveAttachment={removeAttachment}
                          viewer={data.user}
                          onOpenFinalExam={() => navigate("/final-exam")}
                          examLocked={examLocked}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* RIGHT rail */}
        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 self-start" data-testid="sidebar-column">
          <TierRateCard tier={data.user.tier} tierRate={data.user.tier_rate} />

          <ServiceAreaCard
            zip={zip}
            radius={radius}
            onZipChange={setZip}
            onRadiusChange={setRadius}
            onSubmit={saveServiceArea}
            savingArea={savingArea}
            serviceAreaComplete={data.service_area_complete}
          />

          <StatusTimeline status={data.user.status} />
        </aside>
      </div>

      {/* Footer: Take the tour + Replay finale */}
      <footer className="mt-14 pt-6 border-t border-[#1A0F0A]/10 flex items-center justify-between text-[10px] text-[#78584B]">
        <span className="font-mono-tag tracking-[0.25em]">REELTY · PHOTOGRAPHER PORTAL</span>
        <div className="flex items-center gap-5">
          <button
            type="button"
            onClick={() => setShowTutorial(true)}
            data-testid="open-tutorial-btn"
            className="font-mono-tag tracking-[0.25em] uppercase text-[#B08D57] hover:text-[#6B0F1A] transition-colors inline-flex items-center gap-1.5"
            title="Replay the onboarding tour"
          >
            <HelpCircle className="w-3 h-3" strokeWidth={2} />
            Take the tour
          </button>
          <button
            type="button"
            onClick={() => setShowCertCelebration(true)}
            data-testid="replay-finale-btn"
            className="font-mono-tag tracking-[0.25em] uppercase text-[#B08D57] hover:text-[#6B0F1A] transition-colors"
            title="Replay the onboarding-complete celebration"
          >
            Replay finale ↻
          </button>
        </div>
      </footer>

      <PhotographerTutorial
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
      />
    </div>
  );
}
